import {fetchPoll} from '@github-ui/fetch-utils'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type RepoPullsBulkSelectionValue = '0' | '1'

export type RepoPullsBulkUpdate =
  | Readonly<{state: 'open' | 'closed'}>
  | Readonly<{archive: 'true' | 'false'}>
  | Readonly<{labels: Readonly<Record<string, RepoPullsBulkSelectionValue>>}>
  | Readonly<{assignee_logins: Readonly<Record<string, RepoPullsBulkSelectionValue>>}>
  | Readonly<{project_node_ids: readonly string[]}>
  | Readonly<{milestone_node_id: string}>
  | Readonly<{milestone: ''}>

export interface RepoPullsBulkUpdateResult {
  failedPullNodeIds: string[]
}

export interface UpdatePullRequestsOptions {
  owner: string
  repo: string
  pullNumbers: number[]
  pullNodeIds: string[]
  update: RepoPullsBulkUpdate
  onProgress?: (percentage: number) => void
}

function progressPercentage(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object' || !('job' in payload)) return null

  const job = payload.job
  if (!job || typeof job !== 'object' || !('percentage' in job)) return null

  const percentage = job.percentage
  return typeof percentage === 'number' && Number.isInteger(percentage) && percentage >= 0 && percentage <= 100
    ? percentage
    : null
}

function failedPullRequestNodeIds(payload: unknown, submittedPullNodeIds: ReadonlySet<string>): string[] {
  if (!payload || typeof payload !== 'object' || !('job' in payload)) {
    throw new Error('Bulk update returned an invalid job response')
  }

  const job = payload.job
  if (!job || typeof job !== 'object' || !('execution_errors' in job) || !Array.isArray(job.execution_errors)) {
    throw new Error('Bulk update returned an invalid job response')
  }

  const failedNodeIds = new Set<string>()
  for (const executionError of job.execution_errors) {
    if (
      !executionError ||
      typeof executionError !== 'object' ||
      !('node_id' in executionError) ||
      typeof executionError.node_id !== 'string' ||
      !('message' in executionError) ||
      typeof executionError.message !== 'string'
    ) {
      throw new Error('Bulk update returned an invalid job response')
    }
    if (!submittedPullNodeIds.has(executionError.node_id)) {
      throw new Error('Bulk update returned an unexpected failed pull request')
    }
    failedNodeIds.add(executionError.node_id)
  }

  return [...failedNodeIds]
}

export async function updatePullRequests({
  owner,
  repo,
  pullNumbers,
  pullNodeIds,
  update,
  onProgress,
}: UpdatePullRequestsOptions): Promise<RepoPullsBulkUpdateResult> {
  if (pullNodeIds.length !== pullNumbers.length) throw new Error('Bulk update selection is missing pull request IDs')

  const actionUrl = `/${owner}/${repo}/pulls/_bulk_update`
  const response = await reactFetchJSON(actionUrl, {
    method: 'POST',
    body: {issues: pullNumbers, ...update},
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const payload = (await response.json()) as {job?: {url?: unknown}}
  if (typeof payload.job?.url !== 'string') throw new Error('Bulk update did not return a job URL')
  const jobUrl = new URL(payload.job.url, ssrSafeLocation.origin)
  if (jobUrl.origin !== ssrSafeLocation.origin) throw new Error('Bulk update returned a cross-origin job URL')

  const jobResponse = await fetchPoll(`${jobUrl.pathname}${jobUrl.search}`, {
    headers: {Accept: 'application/json'},
    onPoll: onProgress
      ? async pollResponse => {
          let progressPayload: unknown
          try {
            progressPayload = await pollResponse.json()
          } catch {
            return
          }
          const percentage = progressPercentage(progressPayload)
          if (percentage !== null) onProgress(percentage)
        }
      : undefined,
  })

  return {failedPullNodeIds: failedPullRequestNodeIds(await jobResponse.json(), new Set(pullNodeIds))}
}
